/*
 * Purpose: AutoBlocks AI Director — expands home outcome, chooses map pacing (immediate vs journey tube)
 * Last modified: 2026-06-27 | Agent
 * Completeness: 90
 */
import type { DirectorExpansion, OutcomePlan } from './types';

const NATURAL_PACING =
  /\b(remind|follow[\s-]?up|over time|naturally|when it happens|wait for|journey|eventually|passive|monitor|track over|days? later|weeks? later|let it|unfold|patient)\b/i;

const URGENT_PACING =
  /\b(immediately|right away|asap|every (day|morning|hour)|sync now|instant|real[\s-]?time)\b/i;

function inferJourneyPhases(text: string): string[] {
  const phases: string[] = ['Listen for signals in your context'];
  if (/\b(email|gmail|inbox)\b/i.test(text)) {
    phases.push('Let relevant messages arrive');
  }
  if (/\b(remind|follow[\s-]?up)\b/i.test(text)) {
    phases.push('Wait for the right moment to act');
  }
  if (/\b(track|monitor|log)\b/i.test(text)) {
    phases.push('Accumulate data without forcing steps');
  }
  phases.push('Connect blocks when the outcome is ready to crystallize');
  return phases;
}

export function expandOutcomeWithDirector(
  rawOutcome: string,
  interpretedGoal: string
): DirectorExpansion {
  const text = `${rawOutcome} ${interpretedGoal}`.trim();
  const prefersNatural = NATURAL_PACING.test(text) && !URGENT_PACING.test(text);

  if (prefersNatural) {
    const journeyPhases = inferJourneyPhases(text);
    return {
      expandedBrief: interpretedGoal || rawOutcome,
      pacing: 'journey_tube',
      rationale:
        'Director chose natural pacing — let the outcome unfold rather than forcing every step upfront. A Journey Tube stays open from START to GOAL; blocks can attach as signals arrive.',
      journeyPhases,
      directorNote:
        'I will expand the map as your journey progresses. The tube is the path — operational blocks connect when context is clear.',
      startedAt: new Date().toISOString(),
    };
  }

  return {
    expandedBrief: interpretedGoal || rawOutcome,
    pacing: 'immediate',
    rationale:
      'Director is wiring concrete blocks now — triggers, tools, and actions connected on the Mission map.',
    directorNote: 'Expanding your request into a connected block map toward the outcome.',
    startedAt: new Date().toISOString(),
  };
}

/** Journey tube: keep anchors + tube; defer heavy operational wiring */
export function applyDirectorStrategy(plan: OutcomePlan): OutcomePlan {
  if (!plan.director || plan.director.pacing !== 'journey_tube') {
    return plan;
  }

  const triggerOnly = plan.steps.filter((s) => s.type === 'trigger').slice(0, 1);
  const minimalSteps = triggerOnly.length > 0 ? triggerOnly : [];

  return {
    ...plan,
    steps: minimalSteps,
    assumptions: [
      ...plan.assumptions,
      'Journey Tube pacing — Director left a tube to the outcome; add blocks as the journey clarifies.',
    ],
  };
}
